import { jwtDecode } from 'jwt-decode';

import axios from '../utils/axiosInstance';

// Suprime UN registro (el mount inmediato después de un login interactivo),
// para que ese ingreso se cuente solo como login y no también como resume.
const SUPPRESS_NEXT = 'sessionResumeSuppressNext';

// Anti-rebote: dos eventos (focus + visibilitychange) suelen dispararse casi
// al mismo tiempo en un mismo retorno. Con esto contamos un solo resume por
// retorno y evitamos spamear la API en cambios muy rápidos. Ajustable.
const MIN_INTERVAL_MS = 4000;
let lastResumeAt = 0;

/**
 * Marca la sesión para NO registrar el próximo resume (el mount post-login).
 * Se llama tras un login interactivo (B2C).
 */
export const markSessionResumeAccounted = () => {
  sessionStorage.setItem(SUPPRESS_NEXT, '1');
};

/** ¿El JWT propio de la app sigue vigente? */
const isTokenValid = (token) => {
  try {
    const { exp } = jwtDecode(token);
    return typeof exp === 'number' && exp * 1000 > Date.now();
  } catch {
    return false;
  }
};

/**
 * Registra un "resume" (el usuario está usando/volviendo a la app) si hay sesión
 * viva y no estamos dentro de la ventana anti-rebote.
 */
const doRegisterResume = async () => {
  if (Date.now() - lastResumeAt < MIN_INTERVAL_MS) return;

  const userInfo = JSON.parse(localStorage.getItem('userInfo') || 'null');
  if (!userInfo?.accessToken || !isTokenValid(userInfo.accessToken)) return;

  lastResumeAt = Date.now();
  try {
    await axios.post('Aut/session-resume');
  } catch {
    // No es crítico: si falla el registro, no bloqueamos la app.
  }
};

/**
 * Registro al cargar la app (reapertura). Si viene de un login, lo suprime una vez.
 */
export const registerSessionResumeAPI = async () => {
  if (sessionStorage.getItem(SUPPRESS_NEXT)) {
    sessionStorage.removeItem(SUPPRESS_NEXT);
    lastResumeAt = Date.now(); // evita que un focus/visibility inmediato lo cuente
    return;
  }
  await doRegisterResume();
};

/**
 * Empieza a registrar un resume cada vez que el usuario VUELVE a la app:
 * cambia de pestaña/ventana y vuelve, o minimiza y restaura.
 * Devuelve una función de limpieza para quitar los listeners.
 */
export const startSessionResumeTracking = () => {
  const onReturn = () => {
    // Solo cuando la pestaña está visible (ignora el evento de "ocultar").
    if (typeof document !== 'undefined' &&
        document.visibilityState &&
        document.visibilityState !== 'visible') {
      return;
    }
    doRegisterResume();
  };

  const onVisibility = () => {
    if (document.visibilityState === 'visible') doRegisterResume();
  };

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('focus', onReturn);

  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('focus', onReturn);
  };
};
